import { DatePipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { KnowledgeBaseInterface, KnowledgeSource, NamespaceInterface } from '@vendasta/embeddings';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { isTrainingFinished, KnowledgeSourceWithSuggestedFlag } from '../ai-knowledge-util';
import { AiKnowledgeService } from '../services/ai-knowledge.service';
import { PARTNER_NAME_RESOLVER_TOKEN } from '../tokens';
import { kbIdentityKey, namespaceInterfaceToTarget, namespacesEqual } from './namespace.util';
import { OWNED_SENTINEL, TableKnowledgeBase, TableKnowledgeSource, TableKSKind } from './types';

/**
 * Maps the embeddings KB / source protos into the table-row view types, deriving each row's `managedBy`
 * from its namespace via the injected partner-name resolver. Extracted from `KnowledgeManagementComponent`
 * so the management page and the dedicated detail page share one mapping instead of each maintaining its
 * own (the detail page needs the same badge / managed-by logic for visual parity).
 *
 * NOT `providedIn: 'root'` — its dependencies (`AiKnowledgeService`, the per-surface tokens) come from
 * `AiKnowledgeModule.forRoot`, which is imported in a lazy-loaded module in Business Center. A root-scoped
 * instance couldn't see those, so each consuming component provides it in its own `providers` array.
 */
@Injectable()
export class KnowledgeRowMapper {
  private readonly knowledgeService = inject(AiKnowledgeService);
  private readonly translate = inject(TranslateService);
  private readonly partnerNameResolver = inject(PARTNER_NAME_RESOLVER_TOKEN);
  private readonly datePipe = new DatePipe('en-US');

  async toKbRow(kb: KnowledgeBaseInterface, callerNs: NamespaceInterface): Promise<TableKnowledgeBase> {
    const sourceIds = (kb.knowledgeSourceKeys ?? [])
      .map((key) => key.knowledgeSourceId)
      .filter((id): id is string => !!id);
    return {
      id: kb.id ?? '',
      kbKey: kbIdentityKey(kb.namespace, kb.id),
      name: kb.name ?? '',
      description: kb.description ?? '',
      sourceCount: sourceIds.length,
      sourceIds,
      managedBy: await this.resolveManagedBy(kb.namespace, callerNs),
      lastModified: new Date(),
    };
  }

  async toKsRow(ks: KnowledgeSource, callerNs: NamespaceInterface): Promise<TableKnowledgeSource> {
    const websiteCfg = ks.config?.websiteScrapeConfig;
    const autoRefreshTime = websiteCfg?.options?.automaticRefreshTime;
    const nextRefresh = websiteCfg?.refreshScheduledFor;
    let autoRefreshTooltip: string | undefined;
    if (autoRefreshTime) {
      const date =
        this.datePipe.transform(nextRefresh, 'mediumDate') ??
        (await firstValueFrom(this.translate.get('KNOWLEDGE_SHARING.SOURCE_TABLE.AUTO_REFRESH_SCHEDULED')));
      autoRefreshTooltip = await firstValueFrom(
        this.translate.get('KNOWLEDGE_SHARING.SOURCE_TABLE.AUTO_REFRESH_TOOLTIP', { date }),
      );
    }
    return {
      id: ks.id ?? '',
      kind: this.ksKindFromConfig(ks),
      name: ks.name ?? '',
      subtitle: this.knowledgeService.getKnowledgePreview(ks),
      description: ks.description ?? '',
      // A source's owner is its own namespace — no need to find a parent KB to attribute it.
      managedBy: await this.resolveManagedBy(ks.namespace, callerNs),
      lastModified: new Date(),
      isSuggested: (ks as KnowledgeSourceWithSuggestedFlag).isSuggested === true,
      isTrainingInProgress: !isTrainingFinished(ks.training),
      hasAutoRefresh: Boolean(autoRefreshTime),
      autoRefreshTooltip,
      lastScrapedText: websiteCfg ? this.knowledgeService.getLastScraped(ks) : undefined,
    };
  }

  ksKindFromConfig(ks: KnowledgeSource): TableKSKind {
    if (ks.config?.websiteScrapeConfig) return 'url';
    if (ks.config?.fileConfig) {
      const mt = ks.config.fileConfig.mimetype ?? '';
      return mt === 'application/pdf' ? 'pdf' : 'file';
    }
    if (ks.config?.customDataConfig || ks.config?.businessProfileConfig) return 'custom';
    return 'file';
  }

  /** "Owned" when the KB is the caller's, else the namespace's display name via the injected resolver. */
  async resolveManagedBy(
    kbNs: NamespaceInterface | undefined,
    callerNs: NamespaceInterface,
  ): Promise<string> {
    if (!kbNs || namespacesEqual(kbNs, callerNs)) return OWNED_SENTINEL;
    const target = namespaceInterfaceToTarget(kbNs);
    if (!target) return OWNED_SENTINEL;
    try {
      return (await firstValueFrom(this.partnerNameResolver(target).pipe())) ?? target.id;
    } catch {
      return target.id;
    }
  }
}
